import { MoreHorizontal, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ChannelChips, type ChannelHandleMap } from './channel-chips';
import { groupRulesByAction, groupTitle } from '../constants';
import type { SocialRule } from '../types';

/**
 * One grid template shared by the group headers and the rows, so the columns line up without
 * either side hard-coding pixel offsets the other has to match.
 */
const GRID = 'grid grid-cols-[minmax(0,1fr)_448px_124px_44px] items-center';

interface RulesTableProps {
	rules: SocialRule[];
	handles: ChannelHandleMap;
	allChannels: { channelId: string; handle: string; name: string; platform: 'facebook' | 'instagram' }[];
	pendingRuleId: string | null;
	onToggle: (rule: SocialRule) => void;
	onEdit: (rule: SocialRule) => void;
	onDelete: (rule: SocialRule) => void;
}

const GroupHeader = ({ title }: { title: string }) => (
	<div className={`${GRID} h-10 border-y border-gray-300 bg-gray-100 pl-4 text-xs text-gray-800`}>
		<span>{title}</span>
		<span className='border-l border-gray-300 pl-3'>Channels</span>
		<span className='border-l border-gray-300 pl-3'>Enable</span>
		<span />
	</div>
);

interface RuleRowProps extends Omit<RulesTableProps, 'rules'> {
	rule: SocialRule;
}

const RuleRow = ({ rule, handles, allChannels, pendingRuleId, onToggle, onEdit, onDelete }: RuleRowProps) => (
	/*
	 * The row itself is NOT a button: it holds a Switch and a menu Button, and interactive controls
	 * nested inside a `role="button"` are unreachable to assistive tech. The name cell is the
	 * button instead — the only part of the row whose click means "edit".
	 *
	 * The row separator is a MIDDLE-INSET divider (Figma 3428:856), not a full-bleed border:
	 * it stops 16px short at both ends so the rules read as one grouped list rather than as
	 * separate bands. A border-bottom on the row itself runs edge to edge and cannot inset,
	 * so it is drawn as an inset pseudo-element instead.
	 */
	<div
		className={`${GRID} relative h-11 hover:bg-gray-50 after:pointer-events-none after:absolute after:inset-x-4 after:bottom-0 after:h-px after:bg-gray-300 last:after:hidden`}
	>
		<button
			type='button'
			onClick={() => onEdit(rule)}
			aria-label={`Edit ${rule.name}`}
			className='h-full min-w-0 cursor-pointer truncate pl-4 text-left text-[13px] leading-[18px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
		>
			{rule.name}
		</button>

		{/*
		 * The Draft badge lives at the END of the Channels cell, not beside the toggle — Figma
		 * 4241:78806 puts it on the channels side of the divider. `ml-auto` pins it to the far
		 * edge of the column rather than letting it sit flush against the chips, so it reads as
		 * a row-level status and stays in one place however many channels a rule has.
		 *
		 * A draft has never run, which is what explains an off switch nobody set.
		 */}
		<div className='flex items-center gap-1.5 border-l border-gray-300 pl-3 pr-3 min-w-0'>
			<ChannelChips channelScope={rule.channelScope} handles={handles} allChannels={allChannels} />
			{rule.status === 'draft' && (
				<Badge className='ml-auto shrink-0 border-transparent bg-blue-100 px-2 py-1 font-normal text-blue-foreground'>
					Draft
				</Badge>
			)}
		</div>

		<div className='flex items-center gap-2 border-l border-gray-300 pl-3'>
			<Switch
				checked={rule.status === 'enabled'}
				disabled={pendingRuleId === rule.id}
				aria-label={`Enable ${rule.name}`}
				onCheckedChange={() => onToggle(rule)}
			/>
		</div>

		<div>
			{/* Delete only, per the design. Editing is opening the row; a Kim-authored rule has no
			    delete at all, so the menu would be empty and is not rendered. */}
			{rule.deletable && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='ghost' size='icon-sm' aria-label={`Actions for ${rule.name}`}>
							<MoreHorizontal className='size-4' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
						<DropdownMenuItem variant='destructive' onClick={() => onDelete(rule)}>
							<Trash2 className='size-4' /> Delete
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	</div>
);

/**
 * Rules grouped by action.
 *
 * Order inside a group is the server's priority order and is deliberately not re-sorted: priority
 * decides which single rule executes when several match, so any other order would misrepresent
 * what the engine will do.
 */
export const RulesTable = ({ rules, ...rowProps }: RulesTableProps) => (
	<div className='flex flex-col'>
		{groupRulesByAction(rules).map((group) => (
			<div key={group.actionType} className='flex flex-col'>
				<GroupHeader title={groupTitle(group.actionType)} />
				{group.rules.map((rule) => (
					<RuleRow key={rule.id} rule={rule} {...rowProps} />
				))}
			</div>
		))}
	</div>
);
